/**
 * JS facade over Phase 9 local encryption-at-rest commands
 * (src-tauri/src/sync/crypto.rs). Settings → Privacy is the only UI consumer.
 *
 * Threat model (mirror this in any UI copy): protects apiboo.db AT REST
 * (stolen/copied laptop or .db file). It does NOT protect against malware
 * running as the logged-in user while the DB is unlocked, and it does NOT
 * encrypt the plaintext-YAML Git mirror — use a private repo for private data.
 */

import { invoke } from '@tauri-apps/api/core';
import { listen } from '@tauri-apps/api/event';
import { makeSafeUnlisten } from '../utils/safeTauriEvent';
import { isTauri } from '../utils/tauri';

/** Emitted from Rust init when the DB starts encrypted + locked. */
export const SYNC_LOCKED_EVENT = 'sync:locked';

function assertTauri() {
  if (!isTauri()) {
    throw new Error('local encryption requires the desktop app');
  }
}

/** @returns {Promise<{enabled: boolean, locked: boolean}>} */
export function getEncryptionStatus() {
  assertTauri();
  return invoke('sync_encryption_status');
}

/**
 * Enable encryption. Rewrites apiboo.db under a passphrase-derived SQLCipher
 * key (backup → encrypt → verify → atomic swap; original untouched on any
 * failure). Resolves with the 12 recovery words — shown ONCE, never stored:
 * losing both passphrase and recovery phrase makes the data unrecoverable.
 * @returns {Promise<string[]>}
 */
export function enableEncryption(passphrase) {
  assertTauri();
  return invoke('sync_encryption_enable', { passphrase });
}

/** Disable encryption (decrypt rewrite). Passphrase or recovery phrase required. */
export function disableEncryption(passphrase) {
  assertTauri();
  return invoke('sync_encryption_disable', { passphrase });
}

/** Unlock a locked DB with the passphrase OR the 12-word recovery phrase. */
export function unlockSyncDb(secret) {
  assertTauri();
  return invoke('sync_unlock', { secret });
}

/** Drop the connection and wipe the in-memory key (encryption must be on). */
export function lockSyncDb() {
  assertTauri();
  return invoke('sync_lock');
}

// "Remember on this device" (the key cached in the OS keychain) was removed
// on 26.10.2026 — owner: no keychain prompts. With encryption on, the
// passphrase is asked at every start.

/**
 * Subscribe to the locked-at-startup event. Returns a synchronous
 * unsubscribe. No-op outside tauri.
 */
export function onSyncLocked(handler) {
  if (!isTauri()) return () => {};
  const unlistenPromise = listen(SYNC_LOCKED_EVENT, (e) => handler(e.payload));
  return makeSafeUnlisten(async () => {
    const unlisten = await unlistenPromise;
    return unlisten();
  });
}
